import React from 'react';
import { Modal } from 'src/components/design-system';
import styles from './knowledge-category-delete-popup.module.scss';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import {
  getAllKnowledgeItemIdsUnderCategory,
  isKnowledgeOwner,
} from 'shared/store/selectors/knowledge.selector';
import { useKnowledgeItemsById } from 'shared/hooks/knowledge-items.hooks';
import { hasPermissionsToEditItem } from 'shared/store/selectors/iam.selector';
import { Alert } from 'antd';
import analyticsService from 'src/helpers/analytics.service';
import { EVENTS, ANALYTICS_CONTENT_CATEGORIES_TYPES } from 'shared/constants';
import { getCategoryType } from 'shared/utils';

const KnowledgeCategoryDeletePopup = React.memo(function KnowledgeCategoryDeletePopup({
  entity = null,
  isOpen = false,
  deleteCategory = () => {},
  onFinish = () => {},
}) {
  const [isLoading, setLoadingState] = React.useState(false);
  // An editor passes without the item check; an owner needs the rows of every item
  // below the category, fetched by id only while the popup is open.
  const canEditAll = useSelector(hasPermissionsToEditItem);
  const categoriesById = useSelector(state => state.knowledge.categoriesById);
  const itemIds = React.useMemo(
    () =>
      isOpen && entity?.id && !canEditAll
        ? getAllKnowledgeItemIdsUnderCategory({ categoriesById, categoryId: entity.id })
        : [],
    [isOpen, entity?.id, canEditAll, categoriesById],
  );
  const {
    itemsById,
    isLoading: isLoadingItems,
    isError: isItemsError,
  } = useKnowledgeItemsById(itemIds);
  const canRemoveCategory = useSelector(state =>
    isKnowledgeOwner(state, { id: entity?.id }, itemsById),
  );
  const langGeneral = useSelector(getLang('GENERAL'));
  const lang = useSelector(getLang('DELETE_CATEGORY_CONFIRMATION'));

  const deleteCategoryAction = async () => {
    setLoadingState(true);
    await deleteCategory({ id: entity?.id });

    if (entity) {
      analyticsService.track(EVENTS.CATEGORIES.REMOVE, {
        categoryType: getCategoryType({ type: entity.type, path: entity.path }),
        contentType: ANALYTICS_CONTENT_CATEGORIES_TYPES.KNOWLEDGE,
        categoryId: entity.id,
        title: entity.title,
        ownerIds: entity.ownerIds,
      });
    }
    setLoadingState(false);
    onFinish();
  };

  return (
    <Modal
      title={lang.TITLE}
      open={isOpen}
      onCancel={onFinish}
      onSubmit={deleteCategoryAction}
      cancelText={lang.DISMISS}
      submitText={lang.CONFIRM}
      loading={isLoading}
      submitDisabled={isLoadingItems || isItemsError || !canRemoveCategory}
      submitDanger
      restoreFocusAfterDelete>
      <p className={styles.content}>{lang.TEXT.replace('{name}', entity?.title)}</p>

      {isItemsError && (
        <Alert className={styles.alert} message={langGeneral.LOAD_FAILED} type="error" />
      )}
      {!isLoadingItems && !isItemsError && !canRemoveCategory && (
        <Alert className={styles.alert} message={lang.OWNERSHIP_ERROR} type="error" />
      )}
    </Modal>
  );
});

export { KnowledgeCategoryDeletePopup };
